import { useEffect, useRef, useState } from 'react';
import { useTheme } from '../../../context/ThemeContext';

const steps = [
  { number: '01', title: 'Capture', desc: 'Every inbound lead — call, form, or message — is immediately engaged by your AI system the moment they reach out.', icon: 'ri-radar-line', color: '#FF2B2B' },
  { number: '02', title: 'Qualify', desc: 'AI intelligently filters and prioritizes high-value prospects based on your business criteria. Only the best reach your team.', icon: 'ri-filter-3-line', color: '#3FA9F5' },
  { number: '03', title: 'Book', desc: 'Qualified leads are guided through an automated booking flow. Appointments get scheduled — no human required.', icon: 'ri-calendar-2-line', color: '#FF2B2B' },
  { number: '04', title: 'Follow-Up', desc: 'Consistent, personalized messaging sequences ensure no lead goes cold. Every prospect stays engaged until they convert.', icon: 'ri-send-plane-line', color: '#3FA9F5' },
];

export default function HowItWorksSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);
  const { isDark } = useTheme();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="how-it-works" ref={ref} className={`relative py-32 md:py-44 overflow-hidden ${isDark ? 'bg-[#050505]' : 'bg-[#F5F5F7]'}`}>
      <div className={`absolute top-0 left-0 right-0 h-px ${isDark ? 'bg-[#1A1A1A]' : 'bg-[#E8E8ED]'}`}></div>

      <div className="relative z-10 max-w-6xl mx-auto px-6">
        {/* Apple-style: centered headline */}
        <div
          className="text-center mb-24"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(30px)', transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1)' }}
        >
          <span className="text-[#FF2B2B] text-xs font-bold tracking-widest uppercase block mb-6">How It Works</span>
          <h2
            className={`text-4xl md:text-6xl lg:text-7xl font-black leading-tight ${isDark ? 'text-white' : 'text-[#1D1D1F]'}`}
            style={{ letterSpacing: '-0.03em' }}
          >
            Capture. Qualify. <span className="text-[#FF2B2B]">Convert.</span>
          </h2>
        </div>

        {/* Steps — Apple-style large numbered layout */}
        <div className="flex flex-col gap-6">
          {steps.map((step, i) => (
            <div
              key={step.number}
              className={`grid grid-cols-1 md:grid-cols-12 gap-6 items-center p-8 md:p-12 rounded-3xl border transition-all duration-300 ${isDark ? 'bg-[#0D0D0D] border-[#1A1A1A]' : 'bg-white border-[#E8E8ED]'}`}
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'none' : 'translateY(30px)',
                transition: `all 0.7s cubic-bezier(0.16,1,0.3,1) ${i * 0.12}s`,
              }}
            >
              {/* Big number */}
              <div className="md:col-span-2 flex items-center">
                <span
                  className="font-black leading-none"
                  style={{
                    fontSize: 'clamp(56px, 8vw, 96px)',
                    letterSpacing: '-0.05em',
                    color: `${step.color}30`,
                  }}
                >
                  {step.number}
                </span>
              </div>

              {/* Icon */}
              <div className="md:col-span-1 flex justify-center">
                <div className="w-14 h-14 flex items-center justify-center rounded-2xl" style={{ background: `${step.color}15` }}>
                  <i className={`${step.icon} text-2xl`} style={{ color: step.color }}></i>
                </div>
              </div>

              {/* Text */}
              <div className="md:col-span-7 md:pl-4">
                <h3
                  className={`text-2xl md:text-3xl font-black mb-3 ${isDark ? 'text-white' : 'text-[#1D1D1F]'}`}
                  style={{ letterSpacing: '-0.02em' }}
                >
                  {step.title}
                </h3>
                <p className={`text-base leading-relaxed ${isDark ? 'text-[#C9CED6]/60' : 'text-[#6E6E73]'}`} style={{ fontWeight: 300 }}>
                  {step.desc}
                </p>
              </div>

              {/* Step badge */}
              <div className="md:col-span-2 flex justify-end">
                <div
                  className="text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-full"
                  style={{ background: `${step.color}12`, color: step.color }}
                >
                  Step {parseInt(step.number)}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA note */}
        <div
          className="mt-16 text-center"
          style={{ opacity: visible ? 1 : 0, transition: 'all 0.7s cubic-bezier(0.16,1,0.3,1) 0.5s' }}
        >
          <p className={`text-sm ${isDark ? 'text-[#C9CED6]/35' : 'text-[#6E6E73]/60'}`}>
            The entire system runs automatically — <span className={isDark ? 'text-[#C9CED6]/70' : 'text-[#1D1D1F]'}>24 hours a day, 7 days a week.</span>
          </p>
        </div>
      </div>
    </section>
  );
}
